import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const cookieTypes = [
  {
    type: 'Essential Cookies',
    icon: 'ri-lock-line',
    required: true,
    color: 'text-green-600',
    bg: 'bg-green-50 border-green-200',
    badgeBg: 'bg-green-100 text-green-700',
    description: 'These cookies are strictly necessary for the website to function and cannot be disabled.',
    examples: [
      { name: 'session_token', purpose: 'Maintains your authenticated admin session', duration: 'Session' },
      { name: 'csrf_token', purpose: 'Prevents cross-site request forgery attacks', duration: 'Session' },
      { name: 'cookie_consent', purpose: 'Stores your cookie consent preferences', duration: '1 year' },
    ],
  },
  {
    type: 'Performance Cookies',
    icon: 'ri-bar-chart-line',
    required: false,
    color: 'text-amber-600',
    bg: 'bg-amber-50 border-amber-200',
    badgeBg: 'bg-amber-100 text-amber-700',
    description: 'Help us understand how visitors interact with our website by collecting anonymous information.',
    examples: [
      { name: '_ga', purpose: 'Distinguishes unique users for analytics (Google Analytics)', duration: '2 years' },
      { name: '_gid', purpose: 'Stores page view count for analytics', duration: '24 hours' },
      { name: 'page_views', purpose: 'Tracks which pages are most visited', duration: '30 days' },
    ],
  },
  {
    type: 'Functional Cookies',
    icon: 'ri-settings-line',
    required: false,
    color: 'text-teal-600',
    bg: 'bg-teal-50 border-teal-200',
    badgeBg: 'bg-teal-100 text-teal-700',
    description: 'Allow the website to remember choices you make and provide enhanced, personalized features.',
    examples: [
      { name: 'lang_pref', purpose: 'Remembers your language preference', duration: '1 year' },
      { name: 'theme_pref', purpose: 'Stores your display theme preference', duration: '1 year' },
      { name: 'recent_tracks', purpose: 'Remembers recently searched tracking numbers', duration: '30 days' },
    ],
  },
  {
    type: 'Marketing Cookies',
    icon: 'ri-megaphone-line',
    required: false,
    color: 'text-rose-600',
    bg: 'bg-rose-50 border-rose-200',
    badgeBg: 'bg-rose-100 text-rose-700',
    description: 'Used to track visitors across websites to display relevant and engaging advertisements.',
    examples: [
      { name: '_fbp', purpose: 'Used by Facebook to deliver advertisements', duration: '3 months' },
      { name: 'ads_click', purpose: 'Tracks ad campaign click-through rates', duration: '30 days' },
      { name: '_gcl_au', purpose: 'Google Ads conversion tracking', duration: '3 months' },
    ],
  },
];

export default function CookiePolicyPage() {
  const [expandedType, setExpandedType] = useState<string | null>('Essential Cookies');

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="pt-28 pb-14" style={{ background: 'linear-gradient(135deg, #0d2b2b 0%, var(--primary) 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <i className="ri-cookie-line text-white text-2xl" />
          </div>
          <h1 className="font-heading font-bold text-4xl text-white mb-4">Cookie Policy</h1>
          <p className="text-white/70 text-base max-w-xl mx-auto leading-relaxed">
            Learn about how SwiftTrack uses cookies and similar technologies to enhance your tracking experience.
          </p>
          <p className="text-white/40 text-sm mt-4">Last updated: March 25, 2026</p>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="max-w-4xl mx-auto px-6 pt-8 pb-2">
        <nav className="flex items-center gap-2 text-sm text-gray-400">
          <Link to="/" className="hover:text-gray-600 transition-colors">Home</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-600">Cookie Policy</span>
        </nav>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10 pb-20">
        {/* Intro */}
        <div className="border border-gray-100 rounded-2xl p-7 mb-8">
          <h2 className="font-heading font-semibold text-lg text-gray-900 mb-3">What Are Cookies?</h2>
          <p className="text-gray-600 text-sm leading-relaxed">
            Cookies are small text files placed on your device when you visit a website. They help websites remember your preferences, keep you logged in, and understand how you use the site. SwiftTrack uses cookies to provide a seamless shipment tracking experience and to improve our services.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mt-3">
            You can control cookies through your browser settings. Note that disabling certain cookies may limit some features of our platform.
          </p>
        </div>

        {/* Cookie types */}
        <h2 className="font-heading font-semibold text-xl text-gray-900 mb-5">Types of Cookies We Use</h2>
        <div className="space-y-4">
          {cookieTypes.map((ct) => (
            <div key={ct.type} className={`border rounded-2xl overflow-hidden transition-all`}>
              <button
                onClick={() => setExpandedType(expandedType === ct.type ? null : ct.type)}
                className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-gray-50/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--primary-light)' }}>
                    <i className={`${ct.icon} text-base`} style={{ color: 'var(--primary)' }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 text-sm">{ct.type}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ct.badgeBg}`}>
                        {ct.required ? 'Always Active' : 'Optional'}
                      </span>
                    </div>
                    <p className="text-gray-500 text-xs mt-0.5">{ct.description}</p>
                  </div>
                </div>
                <i className={`${expandedType === ct.type ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-gray-400 text-lg flex-shrink-0 ml-4`} />
              </button>

              {expandedType === ct.type && (
                <div className={`border-t px-5 pb-5 ${ct.bg.split(' ')[0]}`}>
                  <table className="w-full mt-4">
                    <thead>
                      <tr>
                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide pb-3 pr-4">Cookie Name</th>
                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide pb-3 pr-4">Purpose</th>
                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide pb-3">Duration</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {ct.examples.map((ex) => (
                        <tr key={ex.name}>
                          <td className="py-3 pr-4">
                            <code className="text-xs font-mono bg-white px-2 py-0.5 rounded border border-gray-200 text-gray-700">{ex.name}</code>
                          </td>
                          <td className="py-3 pr-4 text-xs text-gray-600">{ex.purpose}</td>
                          <td className="py-3 text-xs text-gray-500 whitespace-nowrap">{ex.duration}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Browser control */}
        <div className="mt-8 border border-gray-100 rounded-2xl p-7">
          <h2 className="font-heading font-semibold text-lg text-gray-900 mb-4">Managing Cookie Preferences</h2>
          <p className="text-gray-600 text-sm leading-relaxed mb-5">
            You can manage or delete cookies through your browser settings. Here&apos;s how to access cookie settings in popular browsers:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { name: 'Chrome', icon: 'ri-chrome-line', url: 'https://support.google.com/chrome/answer/95647' },
              { name: 'Firefox', icon: 'ri-firefox-line', url: 'https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences' },
              { name: 'Safari', icon: 'ri-safari-line', url: 'https://support.apple.com/guide/safari/manage-cookies-sfri11471' },
              { name: 'Edge', icon: 'ri-edge-line', url: 'https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge' },
            ].map((browser) => (
              <a
                key={browser.name}
                href={browser.url}
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl hover:border-teal-200 transition-colors cursor-pointer group"
              >
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ color: 'var(--primary)' }}>
                  <i className={`${browser.icon} text-xl`} />
                </div>
                <span className="text-sm text-gray-700 group-hover:text-gray-900">{browser.name}</span>
                <i className="ri-external-link-line text-gray-300 text-xs ml-auto" />
              </a>
            ))}
          </div>
        </div>

        {/* Footer nav */}
        <div className="mt-12 pt-8 border-t border-gray-100 flex flex-wrap gap-4 justify-center">
          <Link to="/privacy-policy" className="text-sm font-medium hover:underline cursor-pointer" style={{ color: 'var(--primary)' }}>
            Privacy Policy
          </Link>
          <span className="text-gray-300">·</span>
          <Link to="/terms-of-service" className="text-sm font-medium hover:underline cursor-pointer" style={{ color: 'var(--primary)' }}>
            Terms of Service
          </Link>
          <span className="text-gray-300">·</span>
          <Link to="/contact" className="text-sm font-medium hover:underline cursor-pointer" style={{ color: 'var(--primary)' }}>
            Contact Us
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
